"use client";

import { ArrowRight, ShieldCheck, Eye, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Inspection() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const values = [{
    icon: Eye,
    title: "Ispezione video avanzata",
    body: "Implementiamo protocolli di mappatura con sistemi robotizzati di ultima generazione e sensori ad alta risoluzione per l'analisi volumetrica dei depositi nei condotti."
  }, {
    icon: ShieldCheck,
    title: "Analisi critica e piano",
    body: "Ogni intervento è guidato da una valutazione tecnica strumentale rigorosa, necessaria per validare il protocollo di riqualificazione igienico-funzionale specifico."
  }, {
    icon: FileText,
    title: "Report fotografico finale",
    body: "Rilasciamo documentazione tecnica certificata e prove visive post-intervento che attestano il ripristino degli standard prestazionali e la conformità normativa."
  }];
  return <section data-section-id="1506" id="tecnologie-di-ispezione-e-monitoraggio" className="relative border-y border-border bg-background py-28 md:py-36 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-32 w-[28rem] h-[28rem] rounded-lg blur-3xl opacity-30" style={{
      background: "radial-gradient(circle, var(--accent) 0%, transparent 65%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-0 w-[26rem] h-[26rem] rounded-lg blur-3xl opacity-25" style={{
      background: "radial-gradient(circle, var(--background) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-[1320px] px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-8 flex flex-col gap-14 lg:gap-16 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="flex flex-col gap-6">
              <p className="font-mono text-xs md:text-[0.8125rem] uppercase tracking-[0.18em] text-muted-foreground" style={{
              letterSpacing: "0.18em"
            }}>
                [VERIFICATION_PROTOCOL_04] — validazione tecnica
              </p>
              <h2 className="font-default text-secondary leading-[1.04] tracking-tight" style={{
              fontSize: "clamp(2.25rem, 4.6vw, 4.25rem)",
              fontWeight: 600
            }}>
                Validazione tecnica per la{" "}
                <span className="relative inline-block text-primary" style={{
                fontWeight: 600
              }}>
                  qualità dell'aria
                  <svg aria-hidden="true" viewBox="0 0 220 14" className="absolute left-0 -bottom-2 w-full h-3" preserveAspectRatio="none">
                    <path d="M3 9 Q 35 2, 70 7 T 140 6 T 215 8" fill="none" stroke="var(--primary)" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </span>{" "}
                e documentazione certificata degli impianti.
              </h2>
            </div>

            {}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {values.map((value, i) => {
              const Icon = value.icon;
              return <div data-index={i} key={value.title} className="group relative bg-card rounded-lg p-7 border border-border transition-all duration-300 ease-out hover:-translate-y-1">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-5" style={{
                  backgroundColor: "var(--accent)"
                }}>
                      <Icon className="size-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-default text-secondary mb-3" style={{
                  fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                  fontWeight: 600,
                  lineHeight: 1.15
                }}>
                      {value.title}
                    </h3>
                    <p className="text-sm md:text-[0.9375rem] leading-[1.65] text-muted-foreground">
                      {value.body}
                    </p>
                  </div>;
            })}
            </div>

            {}
            <div>
              <Button asChild variant="link" className="px-0 h-auto text-foreground hover:text-primary">
                <Link to="/InspectionAndReportPage" className="group inline-flex items-center gap-2">
                  <span className="relative font-default text-base md:text-lg" style={{
                  fontWeight: 500
                }}>
                    Approfondisci il metodo operativo
                    <svg aria-hidden="true" viewBox="0 0 220 8" className="absolute left-0 -bottom-1.5 w-full h-2 transition-all duration-200" preserveAspectRatio="none">
                      <path d="M2 5 Q 40 1, 80 4 T 160 3 T 218 4" fill="none" stroke="var(--primary)" strokeWidth="1.75" strokeLinecap="round" className="group-hover:[stroke-width:3]" />
                    </svg>
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-4 order-first lg:order-last w-1/2 lg:w-full mx-auto lg:mx-0 transition-all duration-700 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="relative">
              <div className="relative rounded-xl overflow-hidden border border-border/40 shadow-xl" style={{
              backgroundColor: "var(--background)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robotic_duct_inspection_system.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robotic_duct_inspection_system.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1720548/img/robotic_duct_inspection_system.png-wvc-sizes" alt="Ispezione interna con strumentazione robotica e videocamera per condotti aria" loading="lazy" decoding="async" className="w-full h-auto object-cover aspect-[3/4] lg:aspect-[3/5]" />
              </div>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground italic">
                 strumentazione di ispezione robotizzata
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}